import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobal } from 'reactn';
import store, { showToast, showToastWithType } from '../../../../core/store';
import { exportCasePlan, fetchCasePlanDetail } from '../../api/casePlanDetailApi';
import { fetchLearnerReentryPlan } from '../../api/learnerReentryPlanApi';
import {
  CASE_PLAN_EXPORT_SUCCESS_MESSAGE,
  REENTRY_PLANS_LIST_PATH,
} from '../../constants/reentry-planning.constants';
import { loadCasePlanDetailData } from '../../utils/case-plan-detail-loader.util';
import { hasCasePlanEditPermission } from '../../utils/reentry-planning.util';

export const useCasePlanDetailsHook = (casePlanId) => {
  const [global] = useGlobal();
  const navigate = useNavigate();
  const isReadOnly = useMemo(
    () => !hasCasePlanEditPermission(global.permissions, global.permissionIds),
    [global.permissions, global.permissionIds],
  );
  const [cardProps, setCardProps] = useState(null);
  const [sections, setSections] = useState([]);
  const [isSectionsLoading, setIsSectionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [isExportOpen, setIsExportOpen] = useState(false);

  const handleBack = useCallback(() => {
    navigate(REENTRY_PLANS_LIST_PATH);
  }, [navigate]);

  const handleToggleCollapse = useCallback((sectionId) => {
    setCollapsedSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  }, []);

  const handleAddWidget = useCallback((_sectionId) => {
    // Widget picker and persistence will be wired in a follow-up slice.
  }, []);

  const handleAddSection = useCallback(() => {
    // Add section flow will be wired in a follow-up slice.
  }, []);

  const handleEditorsClick = useCallback(() => {
    // Editors modal will be wired in a follow-up slice.
  }, []);

  const handleExportClick = useCallback(() => {
    setIsExportOpen(true);
  }, []);

  const handleExportClose = useCallback(() => {
    setIsExportOpen(false);
  }, []);

  const handleExportSubmit = useCallback(async ({ sectionIds, emails, password }) => {
    try {
      await exportCasePlan(casePlanId, { sectionIds, emails, password });
      store.dispatch(showToastWithType(CASE_PLAN_EXPORT_SUCCESS_MESSAGE, 'success'));
    } catch {
      store.dispatch(showToast('Failed to export case plan. Please try again.'));
      throw new Error('Case plan export failed');
    }
  }, [casePlanId]);

  const resetLoadingState = useCallback(() => {
    setIsSectionsLoading(true);
    setError(null);
    setCardProps(null);
    setSections([]);
  }, []);

  const applyLoadedDetail = useCallback((detail) => {
    setCardProps(detail.cardProps);
    setSections(detail.sections);
  }, []);

  const applyLoadFailure = useCallback(() => {
    setCardProps(null);
    setSections([]);
    setError('Failed to load case plan details');
  }, []);

  const finishLoading = useCallback(() => {
    setIsSectionsLoading(false);
  }, []);

  const applyMissingCasePlan = useCallback(() => {
    setError('Case plan not found');
    setIsSectionsLoading(false);
  }, []);

  const loadCasePlanDetail = useCallback(
    async (getIsCancelled, signal) => {
      resetLoadingState();

      try {
        const detail = await loadCasePlanDetailData(casePlanId, {
          fetchCasePlanDetail,
          fetchLearnerReentryPlan,
          signal,
        });

        if (getIsCancelled()) {
          return;
        }

        applyLoadedDetail(detail);
      } catch (error) {
        if (getIsCancelled() || error?.name === 'AbortError') {
          return;
        }

        applyLoadFailure();
      } finally {
        if (!getIsCancelled()) {
          finishLoading();
        }
      }
    },
    [casePlanId, resetLoadingState, applyLoadedDetail, applyLoadFailure, finishLoading],
  );

  useEffect(() => {
    if (!casePlanId) {
      applyMissingCasePlan();
      return;
    }

    const abortController = new AbortController();
    const getIsCancelled = () => abortController.signal.aborted;

    loadCasePlanDetail(getIsCancelled, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [casePlanId, loadCasePlanDetail, applyMissingCasePlan]);

  return {
    cardProps,
    sections,
    isReadOnly,
    isSectionsLoading,
    error,
    collapsedSections,
    handleBack,
    handleToggleCollapse,
    handleAddWidget,
    handleAddSection,
    handleEditorsClick,
    handleExportClick,
    isExportOpen,
    handleExportClose,
    handleExportSubmit,
  };
};
